import { SpeechLocale, SpeechService } from './app/core/speech/speech.service';

const OVERLAY_ID = 'learning-garden-speech-loader';
let observationToken = 0;

function voiceLabel(locale: SpeechLocale): string {
  return locale === 'en-US' ? 'la voce inglese' : 'la voce italiana';
}

function childSurfacePresent(): boolean {
  return Boolean(document.querySelector('.activity-menu-card, .exercise'));
}

function overlayElement(): HTMLElement {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) {
    return overlay;
  }

  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'speech-loading-overlay';
  overlay.hidden = true;
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.setAttribute('aria-atomic', 'true');
  overlay.innerHTML = `
    <div class="speech-loading-card">
      <div class="speech-loading-garden" aria-hidden="true">
        <span class="speech-loading-sprout">🌱</span>
        <span class="speech-loading-dot"></span>
        <span class="speech-loading-dot"></span>
        <span class="speech-loading-dot"></span>
      </div>
      <strong class="speech-loading-title">Preparo la voce…</strong>
      <span class="speech-loading-copy">Un attimo e iniziamo.</span>
      <div class="speech-loading-progress" aria-hidden="true"><span></span></div>
    </div>`;
  document.body.appendChild(overlay);
  return overlay;
}

function hideOverlay(): void {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) {
    overlay.hidden = true;
  }
}

function showOverlay(locale: SpeechLocale, progress: number | null): void {
  const overlay = overlayElement();
  const title = overlay.querySelector<HTMLElement>('.speech-loading-title');
  const copy = overlay.querySelector<HTMLElement>('.speech-loading-copy');
  const bar = overlay.querySelector<HTMLElement>('.speech-loading-progress span');

  if (title) title.textContent = `Sto svegliando ${voiceLabel(locale)}…`;
  if (copy) {
    copy.textContent = progress === null
      ? 'Un attimo e iniziamo.'
      : `Quasi pronta… ${Math.round(progress * 100)}%`;
  }
  if (bar) {
    bar.style.width = progress === null ? '28%' : `${Math.max(8, Math.round(progress * 100))}%`;
    bar.parentElement?.classList.toggle('indeterminate', progress === null);
  }
  overlay.hidden = false;
}

function observeLoading(service: SpeechService, locale: SpeechLocale): void {
  if (typeof document === 'undefined') {
    return;
  }

  const token = ++observationToken;

  const tick = (): void => {
    if (token !== observationToken) {
      return;
    }

    const voice = service.diagnostics().find((item) => item.locale === locale);
    if (!voice || voice.state !== 'loading') {
      hideOverlay();
      return;
    }

    // Angular may not have rendered the destination screen yet when speak()
    // starts a voice. Keep observing the real TTS state instead of giving up.
    if (childSurfacePresent()) {
      showOverlay(locale, voice.progress);
    } else {
      hideOverlay();
    }

    window.setTimeout(tick, 120);
  };

  // Yield once so a synchronous screen signal update can render first.
  window.setTimeout(tick, 0);
}

export function installSpeechLoadingOverlay(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const prototype = SpeechService.prototype;
  const originalSpeak = prototype.speak;

  prototype.speak = function patchedSpeak(
    this: SpeechService,
    text: string,
    locale: SpeechLocale = 'it-IT'
  ): void {
    originalSpeak.call(this, text, locale);
    observeLoading(this, locale);
  };

  const originalCancel = prototype.cancel;
  prototype.cancel = function patchedCancel(this: SpeechService): void {
    observationToken += 1;
    hideOverlay();
    originalCancel.call(this);
  };
}
